// logros.jsx
// ✅ Componente independiente de Logros
// - Muestra puntos acumulados (userData.Cpuntos)
// - Muestra metas: 10, 50, 75, 100
// - Indica habilitado/bloqueado
// - Muestra lo que falta para llegar a cada meta
// - Responsive + estilo ULTRA PRO (sin dependencias externas)

import React, { useMemo } from "react";

const normalizeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const GlobalLogrosCSS = () => (
  <style>{`
    :root{
      --border: rgba(30,64,175,.14);
      --muted: rgba(15,23,42,.72);
      --accent: #06b6d4;
      --accent2: #2563eb;
      --ring: rgba(6,182,212,.35);
      --shadow: 0 16px 40px rgba(2,6,23,.08);
      --shadow2: 0 12px 30px rgba(2,6,23,.10);
    }

    .logros-surface{
      background: linear-gradient(180deg, rgba(255,255,255,.86), rgba(255,255,255,.58));
      border: 1px solid var(--border);
      border-radius: 18px;
      box-shadow: var(--shadow);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      padding: 14px;
      display: grid;
      gap: 14px;
    }

    .logros-title{
      font-size: 22px;
      font-weight: 900;
      margin: 0;
      background: linear-gradient(90deg, var(--accent2), var(--accent));
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      letter-spacing: .2px;
    }

    .badge-soft{
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-weight: 900;
      padding: 8px 12px;
      border-radius: 999px;
      border: 1px solid rgba(37,99,235,.14);
      background: rgba(255,255,255,.7);
      box-shadow: 0 10px 25px rgba(2,6,23,.06);
      color: rgba(15,23,42,.9);
      white-space: nowrap;
    }

    .help{
      color: var(--muted);
      font-size: 14px;
      line-height: 1.6;
    }

    .dash-surface{
      background: linear-gradient(180deg, rgba(255,255,255,.78), rgba(255,255,255,.55));
      border: 1px dashed rgba(37,99,235,.25);
      border-radius: 18px;
      padding: 12px;
    }

    .logros-header{
      display:flex;
      align-items:center;
      justify-content: space-between;
      gap: 10px;
      flex-wrap: wrap;
    }

    .logros-big{
      border-radius: 18px;
      border: 1px solid rgba(37,99,235,.14);
      background: rgba(255,255,255,.70);
      box-shadow: 0 10px 25px rgba(2,6,23,.06);
      padding: 14px;
      display:grid;
      gap: 6px;
    }

    .logros-big .puntos{
      font-size: 28px;
      font-weight: 1000;
      color: rgba(15,23,42,.95);
      line-height: 1;
    }

    .logros-big .sub{
      color: rgba(15,23,42,.65);
      font-weight: 800;
      font-size: 13px;
    }

    .logro-grid{
      display:grid;
      gap: 12px;
      grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
      align-items: stretch;
    }

    .logro-card{
      border-radius: 18px;
      border: 1px solid rgba(37,99,235,.14);
      background: rgba(255,255,255,.70);
      box-shadow: 0 10px 25px rgba(2,6,23,.06);
      padding: 12px;
      display:grid;
      gap: 10px;
    }

    .logro-top{
      display:flex;
      align-items:flex-start;
      justify-content: space-between;
      gap: 10px;
    }

    .logro-title{
      font-weight: 1000;
      color: rgba(15,23,42,.95);
      line-height: 1.2;
      font-size: 15px;
    }

    .logro-desc{
      margin-top: 4px;
      color: rgba(15,23,42,.70);
      font-size: 13px;
      line-height: 1.35;
    }

    .tag{
      display:inline-flex;
      align-items:center;
      gap: 6px;
      padding: 6px 10px;
      border-radius: 999px;
      border: 1px solid rgba(37,99,235,.14);
      background: rgba(255,255,255,.72);
      font-weight: 950;
      color: rgba(15,23,42,.9);
      white-space: nowrap;
      height: fit-content;
    }

    .tag.ok{
      border-color: rgba(16,185,129,.35);
      background: rgba(16,185,129,.10);
    }

    .tag.lock{
      border-color: rgba(239,68,68,.25);
      background: rgba(239,68,68,.08);
    }

    .progress{
      width: 100%;
      height: 12px;
      border-radius: 999px;
      background: rgba(15,23,42,.08);
      overflow: hidden;
      border: 1px solid rgba(37,99,235,.12);
      box-shadow: 0 10px 25px rgba(2,6,23,.04);
    }

    .bar{
      height: 100%;
      width: 0%;
      background: linear-gradient(90deg, rgba(37,99,235,.55), rgba(6,182,212,.55));
    }

    .logro-foot{
      display:flex;
      align-items:center;
      justify-content: space-between;
      gap: 10px;
      color: rgba(15,23,42,.75);
      font-size: 13px;
      font-weight: 850;
      flex-wrap: wrap;
    }
  `}</style>
);

export default function Logros({ userData }) {
  const puntos = normalizeNumber(userData?.Cpuntos);

  

  const LOGROS = useMemo(
    () => [
      {
        puntos: 10,
        titulo: "Vender productos",
        descripcion: "Al llegar a 10 puntos puedes vender productos.",
        icon: "🛒",
      },
      {
        puntos: 50,
        titulo: "Solicitar tarjetas de presentación",
        descripcion: "Al llegar a 50 puntos puedes solicitar tus tarjetas.",
        icon: "💳",
      },
      {
        puntos: 75,
        titulo: "Solicitar label y t-shirt",
        descripcion: "Al llegar a 75 puntos puedes solicitar label y t-shirt.",
        icon: "👕",
      },
      {
        puntos: 100,
        titulo: "Mayor % de ganancias",
        descripcion: "Al llegar a 100 puntos obtienes un porcentaje mayor de ganancias.",
        icon: "💰",
      },
    ],
    []
  );

  const nextGoal = useMemo(() => {
    const pending = LOGROS.find((g) => puntos < g.puntos);
    return pending || null;
  }, [LOGROS, puntos]);

  return (
    <div className="logros-surface">
      <GlobalLogrosCSS />

      <div className="logros-header">
        <h3 className="logros-title" style={{ margin: 0 }}>Logros</h3>

        {nextGoal ? (
          <div className="help">
            Próxima meta: <b>{nextGoal.puntos}</b> pts · te faltan{" "}
            <b>{Math.max(0, nextGoal.puntos - puntos)}</b> pts
          </div>
        ) : (
          <div className="help">✅ Has alcanzado todas las metas disponibles.</div>
        )}
      </div>

      <div className="dash-surface">
        <div className="badge-soft">⭐ Puntos acumulados</div>

        <div className="logros-big" style={{ marginTop: 10 }}>
          <div className="puntos">⭐ {puntos}</div>
          <div className="sub">Puntos disponibles en tu cuenta</div>
        </div>

        <div className="logro-grid" style={{ marginTop: 12 }}>
          {LOGROS.map((g) => {
            const falta = Math.max(0, g.puntos - puntos);
            const ok = puntos >= g.puntos;
            const pct = Math.min(100, Math.round((puntos / g.puntos) * 100));

            return (
              <div key={g.puntos} className="logro-card">
                <div className="logro-top">
                  <div>
                    <div className="logro-title">
                      {g.icon} {g.titulo} <span style={{ opacity: 0.75 }}>· {g.puntos} pts</span>
                    </div>
                    <div className="logro-desc">{g.descripcion}</div>
                  </div>

                  <div className={`tag ${ok ? "ok" : "lock"}`}>
                    {ok ? "✅ Habilitado" : "🔒 Bloqueado"}
                  </div>
                </div>

                <div className="progress" aria-label={`Progreso hacia ${g.puntos} puntos`}>
                  <div className="bar" style={{ width: `${pct}%` }} />
                </div>

                <div className="logro-foot">
                  <span>
                    Progreso: <b>{pct}%</b>
                  </span>
                  <span>
                    {ok ? (
                      <b>Meta alcanzada</b>
                    ) : (
                      <>
                        Te faltan <b>{falta}</b> puntos
                      </>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
